import { useRef } from 'react';
import { cn } from '~/lib/utils';
import styles from './LogoSection.module.css';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';

export interface LogoSectionProps {
  className?: string;
  imageSrc?: string;
  imageAlt?: string;
  /** `dark` is the landing v2 ground: the wordmark goes pale grey, barely there (Guri, 2026-09-04). */
  tone?: 'light' | 'dark';
  /**
   * Hide the SVG but keep its box.
   *
   * For the v2 foot, where `HeroDustField` assembles its dust into this same mark: two of them, one
   * drawn and one made of specks, is the word twice. Hidden rather than unmounted because the dust
   * is registered against this element's rect — remove it and the field has nothing to aim at, and
   * the section collapses to nothing.
   */
  showWordmark?: boolean;
}

const SCROLL_VAR = '--logo-scroll';

export function LogoSection({
  className,
  imageSrc = '/landing/logo-visual/logo-visual.svg',
  imageAlt = 'Deck AI',
  tone = 'light',
  showWordmark = true,
}: LogoSectionProps) {
  const wrapRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    let rafId: number | null = null;

    const prefersReducedMotion = () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const compute = () => {
      rafId = null;
      if (!el.isConnected) return;

      if (prefersReducedMotion()) {
        el.style.setProperty(SCROLL_VAR, '1');
        return;
      }

      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(vh, rect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const h = rect.height;
      const p = h > 0 ? Math.min(1, Math.max(0, visibleHeight / h)) : 0;
      el.style.setProperty(SCROLL_VAR, p.toFixed(4));
    };

    const schedule = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId != null) window.cancelAnimationFrame(rafId);
      el.style.removeProperty(SCROLL_VAR);
    };
  }, []);

  return (
    <section className={cn(styles.section, tone === 'dark' && styles.sectionDark, className)} data-name="LogoSection">
      <div className={styles.container}>
        <div ref={wrapRef} className={styles.wordmarkWrap}>
          <img
            className={cn(styles.wordmark, !showWordmark && styles.wordmarkHidden)}
            src={imageSrc}
            alt={showWordmark ? imageAlt : ''}
            aria-hidden={!showWordmark || undefined}
          />
        </div>
      </div>
    </section>
  );
}

LogoSection.displayName = 'LogoSection';
